import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const LaborSavings = () => {
  const [laborData, setLaborData] = useState({
    hoursSaved: 0,
    efficiencyGain: 0,
    costSavings: 0
  });
  const [chartData, setChartData] = useState({
    manual: [],
    automated: [],
    months: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLaborSavingsData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/dashboard/summary');
        const result = response.data;
        if (result && result.success && result.data) {
          const data = result.data;
          // Map backend data to labor savings fields
          const hoursSaved = data.timeSaved || data.hoursSaved || 0;
          const costSavings = data.costSaved || data.costSavings || 0;
          // Calculate efficiency gain based on hours saved
          const efficiencyGain = hoursSaved > 0 ? Math.min(Math.round((hoursSaved / (hoursSaved + 10)) * 100), 99) : 0;
          setLaborData({
            hoursSaved: hoursSaved,
            efficiencyGain: efficiencyGain,
            costSavings: costSavings
          });
          // Update chart data from backend monthlyData or use empty arrays
          if (data.monthlyData) {
            const manual = data.monthlyData.manual || [];
            const automated = data.monthlyData.automated || [];
            const months = data.monthlyData.months || [];
            // Ensure all arrays have same length
            const maxLength = Math.max(manual.length, automated.length, months.length);
            setChartData({
              manual: manual.length === maxLength ? manual : [],
              automated: automated.length === maxLength ? automated : [],
              months: months.length === maxLength ? months : []
            });
          } else {
            // No monthly data from backend, use empty arrays
            setChartData({
              manual: [],
              automated: [],
              months: []
            });
          }
        }
      } catch (error) {
        console.error('Error fetching labor savings data:', error);
        // Keep default values on error
      } finally {
        setLoading(false);
      }
    };
    fetchLaborSavingsData();
  }, []);

  const maxValue = Math.max(...chartData.manual, ...chartData.automated, 1); // Ensure at least 1 to avoid division by zero
  const hasChartData = chartData.months.length > 0;

  return (
    <div className="dashboard-card savings-card">
      <div className="card-body">
        <div className="card-header">
          <h3 className="card-title">Automation & Labor Savings</h3>
          <p className="card-subtitle">Manual vs Automated listing hours</p>
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            Loading labor savings data...
          </div>
        ) : (
          <>
            <div className="savings-metrics">
              <div className="metric">
                <div className="metric-value">{laborData.hoursSaved}h</div>
                <div className="metric-label">Hours Saved</div>
              </div>
              <div className="metric">
                <div className="metric-value">{laborData.efficiencyGain}%</div>
                <div className="metric-label">Efficiency Gain</div>
              </div>
              <div className="metric">
                <div className="metric-value">${laborData.costSavings.toLocaleString()}</div>
                <div className="metric-label">Cost Savings</div>
              </div>
            </div>

            <div className="area-chart">
              {hasChartData ? (
                <div className="chart-grid">
                  {chartData.months.map((month, index) => (
                    <div key={month} className="chart-column">
                      <div className="bars-container">
                        <div
                          className="manual-bar"
                          style={{
                            height: `${(chartData.manual[index] / maxValue) * 100}%`,
                            animationDelay: `${index * 0.1}s`
                          }}
                          data-tooltip={`Manual: ${chartData.manual[index]}h`}
                        ></div>
                        <div
                          className="automated-bar"
                          style={{
                            height: `${(chartData.automated[index] / maxValue) * 100}%`,
                            animationDelay: `${index * 0.1 + 0.05}s`
                          }}
                          data-tooltip={`Automated: ${chartData.automated[index]}h`}
                        ></div>
                      </div>
                      <div className="month-label">{month}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                  No monthly data available
                </div>
              )}
            </div>

            <div className="chart-legend">
              <div className="legend-item">
                <div className="legend-color manual"></div>
                <span>Manual Listing</span>
              </div>
              <div className="legend-item">
                <div className="legend-color automated"></div>
                <span>Automated Listing</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LaborSavings;